import { ApplicationRef, Injectable, inject } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { BehaviorSubject, concat, fromEvent, interval, merge } from 'rxjs';
import { filter, first, map } from 'rxjs/operators';
import { showConfirmAlert } from '../tools/swal-alerts';

/** Evento `beforeinstallprompt` (aún no está en los tipos del DOM). */
interface PromptInstalacion extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const INTERVALO_CHEQUEO_MS = 30 * 60 * 1000;

/**
 * Manejo del ciclo de vida de la PWA:
 * - avisa y aplica las nuevas versiones (sin esto el dispositivo se queda con
 *   el bundle cacheado hasta que el usuario cierra todas las pestañas);
 * - recupera la app cuando el caché del Service Worker queda corrupto;
 * - guarda el `beforeinstallprompt` para poder ofrecer la instalación desde la
 *   propia UI en Android (`puedeInstalar$` / `instalar()`).
 */
@Injectable({ providedIn: 'root' })
export class PwaService {
  private readonly swUpdate = inject(SwUpdate);
  private readonly appRef = inject(ApplicationRef);

  private prompt: PromptInstalacion | null = null;
  private readonly _puedeInstalar = new BehaviorSubject<boolean>(false);

  /** `true` cuando el navegador ofreció instalar y aún no se instaló. */
  readonly puedeInstalar$ = this._puedeInstalar.asObservable();

  /** `true` si se está corriendo dentro de la app instalada (sin barra). */
  get estaInstalada(): boolean {
    return (
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as Navigator & { standalone?: boolean }).standalone ===
        true
    );
  }

  iniciar(): void {
    this.escucharInstalacion();
    if (!this.swUpdate.isEnabled) return;
    this.escucharVersiones();
    this.chequeoPeriodico();
  }

  /** Lanza el diálogo nativo de instalación de Android. */
  async instalar(): Promise<boolean> {
    if (!this.prompt) return false;
    await this.prompt.prompt();
    const { outcome } = await this.prompt.userChoice;
    this.prompt = null;
    this._puedeInstalar.next(false);
    return outcome === 'accepted';
  }

  private escucharInstalacion(): void {
    window.addEventListener('beforeinstallprompt', (evento: Event) => {
      evento.preventDefault();
      this.prompt = evento as PromptInstalacion;
      this._puedeInstalar.next(true);
    });
    window.addEventListener('appinstalled', () => {
      this.prompt = null;
      this._puedeInstalar.next(false);
    });
  }

  private escucharVersiones(): void {
    this.swUpdate.versionUpdates
      .pipe(filter((e): e is VersionReadyEvent => e.type === 'VERSION_READY'))
      .subscribe(async () => {
        const { isConfirmed } = await showConfirmAlert(
          'Nueva versión disponible',
          'Se actualizó el Portal MAJA. Recarga para usar la versión nueva.',
          'info',
          undefined,
          'Actualizar',
          'Después',
          true,
        );
        if (!isConfirmed) return;
        await this.swUpdate.activateUpdate();
        document.location.reload();
      });

    this.swUpdate.unrecoverable.subscribe(() => document.location.reload());
  }

  private chequeoPeriodico(): void {
    const estable = this.appRef.isStable.pipe(
      first((estable) => estable),
      map(() => undefined),
    );
    const periodico = interval(INTERVALO_CHEQUEO_MS).pipe(map(() => undefined));
    const alVolver = fromEvent(document, 'visibilitychange').pipe(
      filter(() => document.visibilityState === 'visible'),
      map(() => undefined),
    );

    concat(estable, merge(periodico, alVolver)).subscribe(() => this.buscar());
  }

  private buscar(): void {
    this.swUpdate.checkForUpdate().catch(() => undefined);
  }
}
